import {
	configureStore,
	createListenerMiddleware,
	isRejectedWithValue,
	Middleware,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";

import { authReducer, authSlice, resetAuth, setAuth } from "@/pages/Public/redux/auth.slice";

import { companiesReducer, companiesSlice } from "@/pages/Private/redux/companies/companies.slice";

import { coursesReducer, coursesSlice } from "@/pages/Private/redux/courses/courses";

import {
	courseProgressSlice,
	coursesProgressReducer,
} from "@/pages/Private/redux/course-progress/course-progress.slice";

import { ROUTE_CONFIG } from "../routes/config";
import { allTags, api } from "./api";

const { LOGOUT, COURSES } = ROUTE_CONFIG;

const authListenerMiddleware = createListenerMiddleware();

authListenerMiddleware.startListening({
	actionCreator: resetAuth,
	effect: (action, listenerApi) => {
		listenerApi.dispatch(api.util.invalidateTags(["Me"]));
		listenerApi.dispatch(api.util.invalidateTags(allTags.filter((tag) => tag !== "Me")));
	},
});

authListenerMiddleware.startListening({
	actionCreator: setAuth,
	effect: (action, listenerApi) => {
		listenerApi.dispatch(api.util.invalidateTags(["Me"]));
		listenerApi.dispatch(api.util.invalidateTags(allTags.filter((tag) => tag !== "Me")));
	},
});

const rtkRejectQueryMiddleware: Middleware = () => (next) => (action) => {
	if (isRejectedWithValue(action)) {
		const payload = action.payload as { status?: number };
		const meta = action.meta as { arg?: { endpointName?: string } };

		if (payload.status === 401) {
			if (
				![
					"login",
					"authTwoFactorCode",
					"verifyTwoFactor",
					"disableTwoFactor",
					"verifyTwoFactorSettings",
				].includes(meta.arg?.endpointName ?? "")
			) {
				window.location.href = LOGOUT;
			}
		} else if (payload.status === 403) {
			window.location.href = COURSES;
		}
	}

	return next(action);
};

export const store = configureStore({
	reducer: {
		[authSlice.name]: authReducer,
		[api.reducerPath]: api.reducer,
		[companiesSlice.name]: companiesReducer,
		[coursesSlice.name]: coursesReducer,
		[courseProgressSlice.name]: coursesProgressReducer,
	},
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({ serializableCheck: false })
			.concat(rtkRejectQueryMiddleware)
			.concat(authListenerMiddleware.middleware)
			.concat(api.middleware),
});

/**
 * Arms `refetchOnFocus` / `refetchOnReconnect`. Without this call both options are read
 * and silently ignored, which is the whole backstop for anything waiting on a job it was
 * never pushed an event for.
 */
setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
